/* ===== Cart gift wrap offer: adds/removes the gift wrap product from the cart drawer or cart page ===== */
class CartGiftwrapOffer extends HTMLElement {
  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.openGiftMessage = this.openGiftMessage.bind(this);
  }

  connectedCallback() {
    this.toggle = this.querySelector('[data-giftwrap-toggle]');
    this.error = this.querySelector('[data-giftwrap-error]');
    this.toggle?.addEventListener('change', this.handleChange);
    this.messageLink = this.querySelector('[data-giftwrap-message-open]');
    this.messageLink?.addEventListener('click', this.openGiftMessage);

    /* ===== Gift wrap is the only thing left in the cart, so remove it ===== */
    if (this.hasAttribute('data-orphan')) {
      this.setGiftWrap(false);
    }
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('change', this.handleChange);
    this.messageLink?.removeEventListener('click', this.openGiftMessage);
  }

  openGiftMessage() {
    /* ===== Reuse the drawer's existing "Add gift message" modal, untouched ===== */
    const modal = this.closest('cart-drawer')?.querySelector('#CartGiftNote')?.closest('cart-update-modal');
    const openButton = modal?.querySelector('[data-cart-update-open]');
    if (!openButton) return;
    openButton.click();

    /* ===== When it closes, switch this link between add/edit based on the message itself ===== */
    const observer = new MutationObserver(() => {
      if (modal.hasAttribute('open')) return;
      observer.disconnect();
      const hasMessage = !!modal.querySelector('textarea')?.value.trim();
      const text = hasMessage ? modal.dataset.editLabel : modal.dataset.addLabel;
      const linkText = this.querySelector('[data-giftwrap-message-open-text]');
      if (text && linkText) linkText.textContent = text;
      this.messageLink?.focus();
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
  }

  async handleChange() {
    const added = this.toggle.checked;
    this.toggle.disabled = true;
    this.setAttribute('aria-busy', 'true');
    if (this.error) this.error.hidden = true;

    try {
      await this.setGiftWrap(added);
    } catch (error) {
      console.error('Gift wrap update failed:', error);
      this.toggle.checked = !added;
      if (this.error) {
        this.error.textContent = error.message || 'Something went wrong, please try again.';
        this.error.hidden = false;
      }
    } finally {
      this.toggle.disabled = false;
      this.removeAttribute('aria-busy');
    }
  }

  async setGiftWrap(added) {
    const isDrawer = this.dataset.context === 'drawer';

    /* ===== Removing gift wrap: also untag items wrapped from the product page, so the order never asks for unpaid wrapping ===== */
    if (!added) await this.clearGiftWrapTags();

    const url = added ? window.routes.cart_add_url : window.routes.cart_update_url;
    const payload = added
      ? { items: [{ id: this.variantId, quantity: 1 }] }
      : { updates: { [this.variantId]: 0 } };
    if (isDrawer) payload.sections = 'cart-drawer';

    const response = await fetch(`${url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
    const responseJson = await response.json();
    if (!response.ok || responseJson.status) {
      throw new Error(responseJson.description || responseJson.message);
    }

    if (!isDrawer) {
      window.location.reload();
      return;
    }

    window.eventBus.emit('update:cart:drawer', responseJson);
    this.updateCartCount();
  }

  async clearGiftWrapTags() {
    const prop = this.dataset.giftWrapProp;
    if (!prop) return;

    /* ===== Re-read the cart each time, as changing properties can re-key or merge lines ===== */
    for (let i = 0; i < 20; i++) {
      const cart = await fetch(`${window.routes.cart_url}.js`).then((response) => response.json());
      const item = cart.items.find((cartItem) => cartItem.properties?.[prop]);
      if (!item) return;

      /* ===== Shopify keeps omitted properties, so blank it; blank properties are hidden in the cart ===== */
      const properties = { ...item.properties, [prop]: '' };
      const response = await fetch(`${window.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: item.quantity, properties })
      });
      if (!response.ok) throw new Error((await response.json()).description);
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch(`${window.routes.cart_url}?view=compare`);
      window.wetheme?.updateCartCount?.(await response.json());
    } catch (error) {
      console.error('Failed to update cart count:', error);
    }
  }

  get variantId() {
    return parseInt(this.dataset.variantId);
  }
}

if (!customElements.get('cart-giftwrap-offer')) {
  customElements.define('cart-giftwrap-offer', CartGiftwrapOffer);
}
